import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useAPI from '../../hooks/useAPI';
import endpointsAdmin from '../../Services/endpointAdmin';
import api from '../../Services/api';
import {
  applyDistributionToCategoryRows,
  calculateDistributedByCategory,
  normalizeFundingType,
  resolveTransactionFundingType,
} from '../../Services/donationBalance';
import { exportRowsToExcel, exportRowsToPdf, isWithinDateRange } from './exportUtilsAdmin';
import {
  buildAdminMitraUserScope,
  extractArrayPayload,
  getAdminLembagaScope,
  matchItemToAdminScope,
  normalizeScopeValue,
} from './adminScope';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
const CHART_COLORS = ['#0f172a', '#1d4ed8', '#f59e0b', '#fbbf24', '#1e3a8a', '#1e40af', '#fcd34d', '#fde68a'];

function formatCurrency(value) {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(Number(value || 0) || 0);
}

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function sanitizeFilenamePart(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function resolveTransactionDate(trx) {
  return trx.tanggal_transaksi || trx.tgl_transaksi || trx.created_at || trx.tanggal || trx.tgl || null;
}

function resolveAmount(trx) {
  return Number(trx.jumlah || trx.jumlah_donasi || 0) || 0;
}

function resolvePengeluaranNominal(item) {
  const nominalFromDetail = Number(
    item.detail_pengeluaran?.nominal ||
    item.detailPengeluaran?.nominal ||
    item.detail?.nominal
  ) || 0;

  const nominalFromItem = Number(item.nominal) || 0;
  return nominalFromDetail > 0 ? nominalFromDetail : nominalFromItem;
}

function applyPengeluaranToCategoryRows(rows = [], pengeluaranByCategory = new Map()) {
  const remainingPengeluaran = new Map(pengeluaranByCategory);

  return rows.map((row) => {
    const sourceType = row.sourceType || '';
    const pengeluaran = sourceType ? (remainingPengeluaran.get(sourceType) || 0) : 0;
    const used = Math.min(row.total, pengeluaran);

    if (sourceType) {
      remainingPengeluaran.set(sourceType, Math.max(pengeluaran - used, 0));
    }

    return {
      ...row,
      pengeluaran: used,
      total: Math.max(row.total - used, 0),
    };
  });
}

function resolveStatusLabel(trx) {
  const status = String(trx.status || trx.status_transaksi || trx.statusTransaksi || 'pending').toLowerCase();

  if (status === 'success' || status === 'verified' || status === 'diverifikasi' || status === 'berhasil') {
    return 'Sukses';
  }

  if (status === 'diproses' || status === 'processed' || status === 'processing') {
    return 'Diproses';
  }

  return 'Pending';
}

function resolveDetailDonasi(trx) {
  return (
    trx.detailDonasi?.nama_detail_donasi ||
    trx.detail_donasi?.nama_detail_donasi ||
    trx.detailDonasi?.nama ||
    trx.detail_donasi?.nama ||
    trx.nama_detail_donasi ||
    '-'
  );
}

function resolveDonatur(trx) {
  const donatur = trx?.donatur || trx?.data_donatur || trx?.donatur_detail;

  if (typeof trx?.nama_donatur === 'string' && trx.nama_donatur.trim()) {
    return trx.nama_donatur;
  }

  if (donatur && typeof donatur === 'object') {
    return donatur.nama || donatur.nama_donatur || donatur.kode_donatur || donatur.email || '-';
  }

  if (typeof trx?.kode_donatur === 'string' && trx.kode_donatur.trim()) {
    return trx.kode_donatur;
  }

  if (typeof trx?.kode_donatur === 'object' && trx.kode_donatur !== null) {
    return trx.kode_donatur.nama || trx.kode_donatur.kode_donatur || '-';
  }

  return '-';
}

function VerticalBarChart({ data }) {
  const maxValue = Math.max(...data.map((item) => item.total), 1);

  return (
    <div className="rounded-[30px] border border-slate-200 bg-white p-6 shadow-[0_18px_45px_rgba(15,23,42,0.06)]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-amber-600">Grafik Tahunan</p>
          <h2 className="mt-2 text-lg font-semibold text-slate-900">Pemasukan per Bulan</h2>
        </div>
        <div className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">
          12 bulan
        </div>
      </div>

      <div className="mt-6 h-72">
        <div className="flex h-full items-end gap-3">
          {data.map((item, index) => {
            const height = item.total > 0 ? Math.max((item.total / maxValue) * 100, 8) : 0;

            return (
              <div key={item.month} className="flex min-w-0 flex-1 flex-col items-center justify-end gap-3">
                <span className="text-[11px] font-medium text-slate-500">{item.total > 0 ? formatCurrency(item.total) : '-'}</span>
                <div className="flex h-48 w-full items-end rounded-2xl bg-gradient-to-b from-slate-50 to-white px-2 py-2">
                  <div
                    className="w-full rounded-xl bg-gradient-to-t from-slate-950 via-blue-900 to-amber-400 transition-all"
                    style={{ height: `${height}%`, boxShadow: '0 12px 26px rgba(15,23,42,0.16)' }}
                    title={`${item.month}: ${formatCurrency(item.total)}`}
                  />
                </div>
                <span className="text-xs font-semibold text-slate-700">{item.month}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function DonutChart({ data, total }) {
  const radius = 64;
  const circumference = 2 * Math.PI * radius;
  let cumulative = 0;

  return (
    <div className="rounded-[30px] border border-slate-200 bg-white p-6 shadow-[0_18px_45px_rgba(15,23,42,0.06)]">
      <p className="text-xs font-semibold uppercase tracking-[0.24em] text-amber-600">Komposisi Donasi</p>
      <h2 className="mt-2 text-lg font-semibold text-slate-900">Persentase per Jenis</h2>

      <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-center">
        <div className="relative mx-auto h-44 w-44">
          <svg viewBox="0 0 160 160" className="h-full w-full -rotate-90">
            <circle cx="80" cy="80" r={radius} fill="none" stroke="#f1f5f9" strokeWidth="18" />
            {data.map((item, index) => {
              const ratio = total > 0 ? item.total / total : 0;
              const dash = ratio * circumference;
              const offset = -cumulative * circumference;
              cumulative += ratio;

              return (
                <circle
                  key={item.key}
                  cx="80"
                  cy="80"
                  r={radius}
                  fill="none"
                  stroke={CHART_COLORS[index % CHART_COLORS.length]}
                  strokeWidth="18"
                  strokeDasharray={`${dash} ${circumference - dash}`}
                  strokeDashoffset={offset}
                  strokeLinecap="round"
                />
              );
            })}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
            <span className="text-xs uppercase tracking-[0.18em] text-slate-500">Total</span>
            <span className="mt-2 px-4 text-sm font-semibold text-slate-900">{formatCurrency(total)}</span>
          </div>
        </div>

        <div className="flex-1 space-y-3">
          {data.length === 0 ? (
            <p className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-5 text-sm text-slate-500">Belum ada data donasi pada filter ini.</p>
          ) : (
            data.map((item, index) => {
              const percent = total > 0 ? (item.total / total) * 100 : 0;
              return (
                <div key={item.key} className="rounded-2xl border border-slate-200 bg-[linear-gradient(135deg,#ffffff_0%,#fffaf0_100%)] px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <span
                        className="h-3 w-3 rounded-full"
                        style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                      />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-800">{item.label}</p>
                        <p className="text-xs text-slate-500">
                          {item.count} transaksi
                          {item.hakAmil > 0 ? `, hak amil ${formatCurrency(item.hakAmil)}` : ''}
                          {item.distributed > 0 ? `, penyaluran ${formatCurrency(item.distributed)}` : ''}
                          {item.pengeluaran > 0 ? `, pengeluaran ${formatCurrency(item.pengeluaran)}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-slate-900">{formatCurrency(item.total)}</p>
                      <p className="text-xs text-slate-500">{percent.toFixed(1)}%</p>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

function HakAmilChart({ zakatData, infaqData, totalZakatInfaq }) {
  const radius = 64;
  const circumference = 2 * Math.PI * radius;

  // Hak Amil: Zakat 12.5%, Infaq 20%
  const hakAmilZakat = Math.round(zakatData * 0.125);
  const sisaZakat = zakatData - hakAmilZakat;
  const hakAmilInfaq = Math.round(infaqData * 0.20);
  const sisaInfaq = infaqData - hakAmilInfaq;

  // Data untuk breakdown
  const chartData = [
    {
      key: 'hakamil-zakat',
      label: 'Hak Amil Zakat',
      total: hakAmilZakat,
      percentage: totalZakatInfaq > 0 ? (hakAmilZakat / totalZakatInfaq) * 100 : 0,
      color: '#dc2626',
      category: 'Zakat',
    },
    {
      key: 'sisa-zakat',
      label: 'Sisa Zakat',
      total: sisaZakat,
      percentage: totalZakatInfaq > 0 ? (sisaZakat / totalZakatInfaq) * 100 : 0,
      color: '#2563eb',
      category: 'Zakat',
    },
    {
      key: 'hakamil-infaq',
      label: 'Hak Amil Infaq',
      total: hakAmilInfaq,
      percentage: totalZakatInfaq > 0 ? (hakAmilInfaq / totalZakatInfaq) * 100 : 0,
      color: '#ea580c',
      category: 'Infaq',
    },
    {
      key: 'sisa-infaq',
      label: 'Sisa Infaq',
      total: sisaInfaq,
      percentage: totalZakatInfaq > 0 ? (sisaInfaq / totalZakatInfaq) * 100 : 0,
      color: '#7c3aed',
      category: 'Infaq',
    },
  ];

  let cumulative = 0;

  return (
    <div className="rounded-[30px] border border-slate-200 bg-white p-6 shadow-[0_18px_45px_rgba(15,23,42,0.06)]">
      <p className="text-xs font-semibold uppercase tracking-[0.24em] text-green-600">Hak Amil</p>
      <h2 className="mt-2 text-lg font-semibold text-slate-900">Saldo Hak Amil Zakat & Infaq</h2>

      <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-center">
        <div className="relative mx-auto h-44 w-44">
          <svg viewBox="0 0 160 160" className="h-full w-full -rotate-90">
            <circle cx="80" cy="80" r={radius} fill="none" stroke="#f1f5f9" strokeWidth="18" />
            {chartData.map((item) => {
              const ratio = totalZakatInfaq > 0 ? item.total / totalZakatInfaq : 0;
              const dash = ratio * circumference;
              const offset = -cumulative * circumference;
              cumulative += ratio;

              return (
                <circle
                  key={item.key}
                  cx="80"
                  cy="80"
                  r={radius}
                  fill="none"
                  stroke={item.color}
                  strokeWidth="18"
                  strokeDasharray={`${dash} ${circumference - dash}`}
                  strokeDashoffset={offset}
                  strokeLinecap="round"
                />
              );
            })}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
            <span className="text-xs uppercase tracking-[0.18em] text-slate-500">Total</span>
            <span className="mt-2 px-4 text-sm font-semibold text-slate-900">{formatCurrency(totalZakatInfaq)}</span>
          </div>
        </div>

        <div className="flex-1 space-y-3">
          {totalZakatInfaq === 0 ? (
            <p className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-5 text-sm text-slate-500">Belum ada data zakat dan infaq pada filter ini.</p>
          ) : (
            <>
              <div className="space-y-2">
                <p className="text-xs font-semibold text-slate-700">ZAKAT (12.5% Hak Amil)</p>
                {chartData
                  .filter((item) => item.category === 'Zakat')
                  .map((item) => (
                    <div key={item.key} className="rounded-2xl border border-slate-200 bg-[linear-gradient(135deg,#ffffff_0%,#fffaf0_100%)] px-4 py-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-3">
                          <span
                            className="h-3 w-3 rounded-full"
                            style={{ backgroundColor: item.color }}
                          />
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-slate-800">{item.label}</p>
                            <p className="text-xs text-slate-500">Dari Zakat</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-slate-900">{formatCurrency(item.total)}</p>
                          <p className="text-xs text-slate-500">{item.percentage.toFixed(1)}%</p>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>

              <div className="space-y-2 border-t border-slate-100 pt-3">
                <p className="text-xs font-semibold text-slate-700">INFAQ (20% Hak Amil)</p>
                {chartData
                  .filter((item) => item.category === 'Infaq')
                  .map((item) => (
                    <div key={item.key} className="rounded-2xl border border-slate-200 bg-[linear-gradient(135deg,#ffffff_0%,#fffaf0_100%)] px-4 py-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-3">
                          <span
                            className="h-3 w-3 rounded-full"
                            style={{ backgroundColor: item.color }}
                          />
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-slate-800">{item.label}</p>
                            <p className="text-xs text-slate-500">Dari Infaq</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-slate-900">{formatCurrency(item.total)}</p>
                          <p className="text-xs text-slate-500">{item.percentage.toFixed(1)}%</p>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PembagianHakAmilChart({ zakatData, infaqData }) {
  const radius = 64;
  const circumference = 2 * Math.PI * radius;

  // Hitung Hak Amil
  const hakAmilZakat = Math.round(zakatData * 0.125);
  const hakAmilInfaq = Math.round(infaqData * 0.20);
  const totalHakAmil = hakAmilZakat + hakAmilInfaq;

  // Bagi 60% untuk DT Peduli, 40% untuk lainnya
  const hakAmilZakatDtPeduli = Math.round(hakAmilZakat * 0.60);
  const hakAmilZakatLainnya = hakAmilZakat - hakAmilZakatDtPeduli;
  const hakAmilInfaqDtPeduli = Math.round(hakAmilInfaq * 0.60);
  const hakAmilInfaqLainnya = hakAmilInfaq - hakAmilInfaqDtPeduli;

  // Data untuk breakdown
  const chartData = [
    {
      key: 'zakat-dtpeduli',
      label: 'DT Peduli (Zakat)',
      total: hakAmilZakatDtPeduli,
      percentage: totalHakAmil > 0 ? (hakAmilZakatDtPeduli / totalHakAmil) * 100 : 0,
      color: '#dc2626',
      category: 'Zakat',
      subCategory: 'DT Peduli',
    },
    {
      key: 'zakat-lainnya',
      label: 'Amil MPZ (Zakat)',
      total: hakAmilZakatLainnya,
      percentage: totalHakAmil > 0 ? (hakAmilZakatLainnya / totalHakAmil) * 100 : 0,
      color: '#f87171',
      category: 'Zakat',
      subCategory: 'Lainnya',
    },
    {
      key: 'infaq-dtpeduli',
      label: 'DT Peduli (Infaq)',
      total: hakAmilInfaqDtPeduli,
      percentage: totalHakAmil > 0 ? (hakAmilInfaqDtPeduli / totalHakAmil) * 100 : 0,
      color: '#ea580c',
      category: 'Infaq',
      subCategory: 'DT Peduli',
    },
    {
      key: 'infaq-lainnya',
      label: 'Amil MPZ (Infaq)',
      total: hakAmilInfaqLainnya,
      percentage: totalHakAmil > 0 ? (hakAmilInfaqLainnya / totalHakAmil) * 100 : 0,
      color: '#fdba74',
      category: 'Infaq',
      subCategory: 'Lainnya',
    },
  ];

  let cumulative = 0;

  return (
    <div className="rounded-[30px] border border-slate-200 bg-white p-6 shadow-[0_18px_45px_rgba(15,23,42,0.06)]">
      <p className="text-xs font-semibold uppercase tracking-[0.24em] text-blue-600">Pembagian Hak Amil</p>
      <h2 className="mt-2 text-lg font-semibold text-slate-900">Distribusi 60% untuk DT Peduli</h2>

      <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-center">
        <div className="relative mx-auto h-44 w-44">
          <svg viewBox="0 0 160 160" className="h-full w-full -rotate-90">
            <circle cx="80" cy="80" r={radius} fill="none" stroke="#f1f5f9" strokeWidth="18" />
            {chartData.map((item) => {
              const ratio = totalHakAmil > 0 ? item.total / totalHakAmil : 0;
              const dash = ratio * circumference;
              const offset = -cumulative * circumference;
              cumulative += ratio;

              return (
                <circle
                  key={item.key}
                  cx="80"
                  cy="80"
                  r={radius}
                  fill="none"
                  stroke={item.color}
                  strokeWidth="18"
                  strokeDasharray={`${dash} ${circumference - dash}`}
                  strokeDashoffset={offset}
                  strokeLinecap="round"
                />
              );
            })}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
            <span className="text-xs uppercase tracking-[0.18em] text-slate-500">Total</span>
            <span className="mt-2 px-4 text-sm font-semibold text-slate-900">{formatCurrency(totalHakAmil)}</span>
          </div>
        </div>

        <div className="flex-1 space-y-3">
          {totalHakAmil === 0 ? (
            <p className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-5 text-sm text-slate-500">Belum ada data hak amil pada filter ini.</p>
          ) : (
            <>
              <div className="space-y-2">
                <p className="text-xs font-semibold text-slate-700">AMIL ZAKAT</p>
                {chartData
                  .filter((item) => item.category === 'Zakat')
                  .map((item) => (
                    <div key={item.key} className="rounded-2xl border border-slate-200 bg-[linear-gradient(135deg,#ffffff_0%,#fef2f2_100%)] px-4 py-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-3">
                          <span
                            className="h-3 w-3 rounded-full"
                            style={{ backgroundColor: item.color }}
                          />
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-slate-800">{item.label}</p>
                            <p className="text-xs text-slate-500">
                              {item.subCategory === 'DT Peduli' ? '60% Hak Amil' : '40% Hak Amil'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-slate-900">{formatCurrency(item.total)}</p>
                          <p className="text-xs text-slate-500">{item.percentage.toFixed(1)}%</p>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>

              <div className="space-y-2 border-t border-slate-100 pt-3">
                <p className="text-xs font-semibold text-slate-700">AMIL INFAQ</p>
                {chartData
                  .filter((item) => item.category === 'Infaq')
                  .map((item) => (
                    <div key={item.key} className="rounded-2xl border border-slate-200 bg-[linear-gradient(135deg,#ffffff_0%,#fff7ed_100%)] px-4 py-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-3">
                          <span
                            className="h-3 w-3 rounded-full"
                            style={{ backgroundColor: item.color }}
                          />
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-slate-800">{item.label}</p>
                            <p className="text-xs text-slate-500">
                              {item.subCategory === 'DT Peduli' ? '60% Hak Amil' : '40% Hak Amil'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-slate-900">{formatCurrency(item.total)}</p>
                          <p className="text-xs text-slate-500">{item.percentage.toFixed(1)}%</p>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function LaporanDonasiAdmin({ admin }) {
  const { data: transaksiData, loading, error } = useAPI('/api/transaksi');
  const { data: jenisDonasiData } = useAPI(endpointsAdmin.jenis_donasi.getAll);
  const { data: detailDonasiData } = useAPI(endpointsAdmin.detail_donasi.getAll);
  const { data: penyaluranData } = useAPI(endpointsAdmin.penyaluran_program.getAll);
  const { data: programData } = useAPI(endpointsAdmin.program.getAll);
  const { data: pengeluaranData, refetch: refetchPengeluaran } = useAPI(endpointsAdmin.pengeluaran.getAll);
  const { data: detailPengeluaranData, refetch: refetchDetailPengeluaran } = useAPI(endpointsAdmin.detail_pengeluaran.getAll);
  const [adminMitraRecords, setAdminMitraRecords] = useState([]);
  const [loadingAdminMitra, setLoadingAdminMitra] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isExportOpen, setIsExportOpen] = useState(false);
  const lembagaScope = getAdminLembagaScope(admin);

  const transaksiList = useMemo(() => {
    if (Array.isArray(transaksiData)) return transaksiData;
    if (Array.isArray(transaksiData?.transaksi)) return transaksiData.transaksi;
    if (Array.isArray(transaksiData?.data)) return transaksiData.data;
    if (Array.isArray(transaksiData?.result)) return transaksiData.result;
    return [];
  }, [transaksiData]);

  const jenisDonasiList = useMemo(() => {
    if (Array.isArray(jenisDonasiData)) return jenisDonasiData;
    if (Array.isArray(jenisDonasiData?.jenis_donasi)) return jenisDonasiData.jenis_donasi;
    if (Array.isArray(jenisDonasiData?.data)) return jenisDonasiData.data;
    if (Array.isArray(jenisDonasiData?.result)) return jenisDonasiData.result;
    return [];
  }, [jenisDonasiData]);

  const detailDonasiList = useMemo(
    () => extractArrayPayload(detailDonasiData, ['detail_donasi']),
    [detailDonasiData]
  );
  const penyaluranList = useMemo(
    () => extractArrayPayload(penyaluranData, ['penyaluran_program']),
    [penyaluranData]
  );
  const programList = useMemo(
    () => extractArrayPayload(programData, ['program']),
    [programData]
  );
  const pengeluaranList = useMemo(
    () => extractArrayPayload(pengeluaranData, ['pengeluaran']),
    [pengeluaranData]
  );

  const detailPengeluaranList = useMemo(
    () => extractArrayPayload(detailPengeluaranData, ['detail_pengeluaran']),
    [detailPengeluaranData]
  );

  // Buat map detail_pengeluaran untuk lookup cepat
  const detailPengeluaranByIdMap = useMemo(
    () => new Map(
      detailPengeluaranList.map((item) => [
        String(item.id_pengeluaran || item.idPengeluaran || item.pengeluaran_id || ''),
        item
      ])
    ),
    [detailPengeluaranList]
  );

  // Enrich pengeluaran dengan detail_pengeluaran-nya
  const enrichedPengeluaranList = useMemo(
    () => pengeluaranList.map((item) => ({
      ...item,
      detail_pengeluaran: item.detail_pengeluaran || detailPengeluaranByIdMap.get(String(item.id_pengeluaran || item.id || ''))
    })),
    [pengeluaranList, detailPengeluaranByIdMap]
  );

  const getJenisMeta = useCallback((trx) => {
    const kodeJenis =
      trx?.kode_jenis_donasi ||
      trx?.kodeJenisDonasi ||
      trx?.jenis_donasi?.kode_jenis_donasi ||
      trx?.jenisDonasi?.kode_jenis_donasi ||
      trx?.jenis_donasi?.kode ||
      trx?.jenisDonasi?.kode ||
      trx?.kode_jenis ||
      '';

    const matchedJenis = jenisDonasiList.find((item) => {
      const itemKode =
        item?.kode_jenis_donasi ||
        item?.kodeJenisDonasi ||
        item?.kode ||
        item?.kode_jenis ||
        '';

      return normalizeScopeValue(itemKode) === normalizeScopeValue(kodeJenis);
    });

    const namaJenis =
      trx?.jenisDonasi?.nama_donasi ||
      trx?.jenis_donasi?.nama_donasi ||
      matchedJenis?.nama_donasi ||
      matchedJenis?.namaDonasi ||
      trx?.jenisDonasi?.nama ||
      trx?.jenis_donasi?.nama ||
      matchedJenis?.nama ||
      matchedJenis?.nama_jenis ||
      trx?.nama_jenis_donasi ||
      'Donasi Lainnya';

    return {
      kode: String(kodeJenis || '').trim(),
      nama: namaJenis,
    };
  }, [jenisDonasiList]);

  useEffect(() => {
    let cancelled = false;

    const fetchAdminMitra = async () => {
      if (!lembagaScope.active) {
        setAdminMitraRecords([]);
        return;
      }

      try {
        setLoadingAdminMitra(true);
        const response = await api.post(endpointsAdmin.adminmitra.createByLembaga(lembagaScope.id), {});
        if (cancelled) return;
        setAdminMitraRecords(extractArrayPayload(response.data, ['adminmitra', 'data_adminmitra', 'users']));
      } catch (fetchError) {
        if (cancelled) return;
        setAdminMitraRecords([]);
      } finally {
        if (!cancelled) {
          setLoadingAdminMitra(false);
        }
      }
    };

    fetchAdminMitra();

    return () => {
      cancelled = true;
    };
  }, [lembagaScope.active, lembagaScope.id]);

  // Polling untuk update data pengeluaran setiap 1 detik
  // Memastikan sisa hak amil selalu terupdate ketika ada pengeluaran baru
  useEffect(() => {
    const pollInterval = setInterval(() => {
      refetchPengeluaran();
      refetchDetailPengeluaran();
    }, 1000); // 1 detik - lebih responsif

    // Refetch juga ketika tab kembali fokus
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refetchPengeluaran();
        refetchDetailPengeluaran();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Refetch juga saat window fokus kembali
    const handleFocus = () => {
      refetchPengeluaran();
      refetchDetailPengeluaran();
    };
    
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(pollInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [refetchPengeluaran, refetchDetailPengeluaran]);

  const adminMitraScope = useMemo(
    () => buildAdminMitraUserScope(adminMitraRecords),
    [adminMitraRecords]
  );

  const scopedTransactions = useMemo(() => (
    transaksiList.filter((trx) => matchItemToAdminScope(trx, lembagaScope, adminMitraScope))
  ), [transaksiList, lembagaScope, adminMitraScope]);

  const scopedPenyaluranList = useMemo(() => (
    penyaluranList.filter((item) => matchItemToAdminScope(item, lembagaScope, adminMitraScope))
  ), [penyaluranList, lembagaScope, adminMitraScope]);

  const scopedPengeluaranList = useMemo(() => (
    enrichedPengeluaranList.filter((item) => matchItemToAdminScope(item, lembagaScope, adminMitraScope))
  ), [enrichedPengeluaranList, lembagaScope, adminMitraScope]);

  const distributedByCategory = useMemo(
    () => calculateDistributedByCategory(scopedPenyaluranList, programList),
    [scopedPenyaluranList, programList]
  );

  const filteredTransactions = useMemo(() => (
    scopedTransactions.filter((trx) => {
      const rawDate = resolveTransactionDate(trx);
      const date = rawDate ? new Date(rawDate) : null;
      if (!date || Number.isNaN(date.getTime())) return false;

      // Filter hanya transaksi yang sukses
      const status = resolveStatusLabel(trx);
      if (status !== 'Sukses') return false;

      const jenisMeta = getJenisMeta(trx);
      const normalizedQuery = searchQuery.trim().toLowerCase();
      const detailDonasi = resolveDetailDonasi(trx).toLowerCase();
      const donatur = resolveDonatur(trx).toLowerCase();
      const catatan = String(trx.catatan || trx.catatan_donatur || '').toLowerCase();
      const matchesSearch =
        !normalizedQuery ||
        jenisMeta.nama.toLowerCase().includes(normalizedQuery) ||
        String(jenisMeta.kode || '').toLowerCase().includes(normalizedQuery) ||
        detailDonasi.includes(normalizedQuery) ||
        donatur.includes(normalizedQuery) ||
        catatan.includes(normalizedQuery);

      return isWithinDateRange(rawDate, startDate, endDate) && matchesSearch;
    })
  ), [scopedTransactions, startDate, endDate, searchQuery, getJenisMeta]);

  const laporan = useMemo(() => {
    const monthlyTotals = Array(12).fill(0);
    const categoryMap = new Map();
    let grossTotalNominal = 0;
    let transaksiSukses = 0;
    let totalZakat = 0;
    let totalInfaq = 0;

    filteredTransactions.forEach((trx) => {
      const date = new Date(resolveTransactionDate(trx));
      const amount = resolveAmount(trx);
      const status = resolveStatusLabel(trx);
      const jenisMeta = getJenisMeta(trx);
      const key = normalizeScopeValue(jenisMeta.kode || jenisMeta.nama);
      const sourceType = resolveTransactionFundingType(trx, detailDonasiList, jenisMeta.nama);
      const current = categoryMap.get(key) || {
        key,
        label: jenisMeta.nama,
        sourceType,
        total: 0,
        count: 0,
      };

      grossTotalNominal += amount;
      monthlyTotals[date.getMonth()] += amount;
      current.total += amount;
      current.count += 1;
      if (!current.sourceType && sourceType) current.sourceType = sourceType;
      categoryMap.set(key, current);

      // Hitung zakat dan infaq
      const normalizedJenisNama = normalizeScopeValue(jenisMeta.nama || '');
      if (normalizedJenisNama.includes('zakat')) {
        totalZakat += amount;
      }
      if (normalizedJenisNama.includes('infaq') || normalizedJenisNama.includes('infak')) {
        totalInfaq += amount;
      }

      if (status === 'Sukses') {
        transaksiSukses += 1;
      }
    });

    const pengeluaranByCategory = scopedPengeluaranList.reduce((totals, item) => {
      const sourceType = normalizeFundingType(item.sumber_dana);

      if (['zakat', 'infak', 'wakaf'].includes(sourceType)) {
        totals.set(sourceType, (totals.get(sourceType) || 0) + resolvePengeluaranNominal(item));
      }

      return totals;
    }, new Map());

    const categoryTotalsAfterDistribution = applyDistributionToCategoryRows(
      Array.from(categoryMap.values()).sort((a, b) => b.total - a.total),
      distributedByCategory
    );
    const categoryTotals = applyPengeluaranToCategoryRows(categoryTotalsAfterDistribution, pengeluaranByCategory);
    const totalTersalur = Array.from(distributedByCategory.values()).reduce((sum, value) => sum + value, 0);
    const totalNominal = categoryTotals.reduce((sum, item) => sum + item.total, 0);
    const monthlyChartData = MONTH_LABELS.map((month, index) => ({
      month,
      total: monthlyTotals[index],
    }));

    // Hitung total hak amil
    const hakAmilZakat = Math.round(totalZakat * 0.125);
    const hakAmilInfaq = Math.round(totalInfaq * 0.20);
    const totalHakAmil = hakAmilZakat + hakAmilInfaq;
    
    // Hitung DT Peduli (60% dari masing-masing hak amil)
    const dtPeduliDariZakat = Math.round(hakAmilZakat * 0.60);
    const dtPeduliDariInfaq = Math.round(hakAmilInfaq * 0.60);
    const totalDtPeduli = dtPeduliDariZakat + dtPeduliDariInfaq;

    // Hitung sisa hak amil (40% yang tersisa setelah dialokasikan ke DT Peduli)
    const sisaHakAmilZakat = hakAmilZakat - dtPeduliDariZakat;
    const sisaHakAmilInfaq = hakAmilInfaq - dtPeduliDariInfaq;
    let totalHakAmilSisa = sisaHakAmilZakat + sisaHakAmilInfaq;

    // Subtract pengeluaran dari dana_amil
    // Detail nominal ada di detail_pengeluaran.nominal
    const pengeluaranDanaAmil = scopedPengeluaranList.reduce((total, item) => {
      const normalizedSource = normalizeFundingType(item.sumber_dana);
      return normalizedSource === 'dana_amil' ? total + resolvePengeluaranNominal(item) : total;
    }, 0);

    totalHakAmilSisa = Math.max(totalHakAmilSisa - pengeluaranDanaAmil, 0);

    return {
      totalNominal,
      grossTotalNominal,
      totalTersalur,
      totalTransaksi: filteredTransactions.length,
      transaksiSukses,
      rerataNominal: filteredTransactions.length ? totalNominal / filteredTransactions.length : 0,
      monthlyChartData,
      categoryTotals,
      totalZakat,
      totalInfaq,
      totalZakatInfaq: totalZakat + totalInfaq,
      totalHakAmil,
      totalDtPeduli,
      dtPeduliDariZakat,
      dtPeduliDariInfaq,
      totalHakAmilSisa,
      sisaHakAmilZakat,
      sisaHakAmilInfaq,
    };
  }, [detailDonasiList, distributedByCategory, filteredTransactions, getJenisMeta, scopedPengeluaranList]);

  const topMonthly = useMemo(() => {
    const candidate = laporan.monthlyChartData.reduce((best, item) => (
      item.total > best.total ? item : best
    ), { month: '-', total: 0 });

    return candidate;
  }, [laporan.monthlyChartData]);

  const exportTransactionsToPdf = useCallback((rows, categoryLabel = 'Semua Kategori') => {
    const title = `Laporan Donasi - ${categoryLabel}`;

    exportRowsToPdf(
      title,
      ['Kode Transaksi', 'Tanggal', 'Donatur', 'Jenis Donasi', 'Program', 'Nominal', 'Status'],
      rows.map((trx) => [
        trx.kode_transaksi || '-',
        formatDate(resolveTransactionDate(trx)),
        resolveDonatur(trx),
        getJenisMeta(trx).nama,
        resolveDetailDonasi(trx),
        formatCurrency(resolveAmount(trx)),
        resolveStatusLabel(trx),
      ])
    );
  }, [getJenisMeta]);

  const exportTransactionsToExcel = useCallback((rows, categoryLabel = 'Semua Kategori') => {
    const title = `Laporan Donasi - ${categoryLabel}`;
    const filename = `laporan-donasi-${sanitizeFilenamePart(categoryLabel) || 'semua-kategori'}`;

    exportRowsToExcel(
      filename,
      title,
      ['Kode Transaksi', 'Tanggal', 'Donatur', 'Jenis Donasi', 'Program', 'Nominal', 'Status'],
      rows.map((trx) => [
        trx.kode_transaksi || '-',
        formatDate(resolveTransactionDate(trx)),
        resolveDonatur(trx),
        getJenisMeta(trx).nama,
        resolveDetailDonasi(trx),
        resolveAmount(trx),
        resolveStatusLabel(trx),
      ])
    );
  }, [getJenisMeta]);

  const handleExportPdf = () => {
    exportTransactionsToPdf(filteredTransactions, 'Hasil Filter');
  };

  const handleExportExcel = () => {
    exportTransactionsToExcel(filteredTransactions, 'Hasil Filter');
  };

  if (loading || loadingAdminMitra) {
    return <div className="w-full px-6 py-12 text-slate-600 lg:px-16">Memuat laporan donasi...</div>;
  }

  if (error) {
    return <div className="w-full px-6 py-12 text-red-600 lg:px-16">{error}</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      <div className="w-full space-y-10 px-6 py-12 lg:px-16">
        <section className="overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-900 via-blue-900 to-blue-950 text-white shadow-[0_20px_50px_rgba(15,23,42,0.15)]">
          <div className="grid gap-8 px-8 py-10 lg:grid-cols-[1.5fr_1fr] lg:py-12">
            <div>
              <div className="inline-block rounded-full bg-white/10 backdrop-blur px-4 py-2 text-xs font-semibold uppercase tracking-wider text-blue-200 mb-4">
                Laporan Donasi
              </div>
              <h1 className="text-3xl font-bold leading-tight lg:text-4xl">
                Dashboard Pemasukan Donasi
              </h1>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-200">
                Pantau seluruh pemasukan donasi dengan visualisasi data yang komprehensif. Filter berdasarkan periode dan pencarian, serta export laporan dalam format Excel dan PDF.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-white/15 bg-white/10 p-5 backdrop-blur-md transition hover:bg-white/15">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-blue-200">Rentang Filter</p>
                <p className="mt-3 text-base font-bold">{startDate || '—'} hingga {endDate || '—'}</p>
                <p className="mt-2 text-xs text-slate-300">Periode data yang sedang aktif</p>
              </div>
              <div className="rounded-2xl border border-white/15 bg-white/10 p-5 backdrop-blur-md transition hover:bg-white/15">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-blue-200">Puncak Bulan</p>
                <p className="mt-3 text-base font-bold">{topMonthly.month}</p>
                <p className="mt-2 text-xs text-slate-300">{formatCurrency(topMonthly.total)}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-[30px] border border-slate-200 bg-white p-8 shadow-[0_18px_45px_rgba(15,23,42,0.06)]">
          <div className="mb-6 flex flex-col gap-1">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-amber-600">Kontrol Filter</p>
            <h2 className="text-xl font-bold text-slate-900">Atur periode dan pencarian data</h2>
            <p className="mt-1 text-sm text-slate-600">Gunakan filter di bawah untuk menyaring laporan transaksi donasi.</p>
          </div>

          <div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-3">
              <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm transition focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100">
                <span className="text-slate-400">📅</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                  className="w-full min-w-[150px] bg-transparent text-sm text-slate-700 outline-none"
                />
              </div>

              <div className="hidden px-2 text-sm font-semibold text-slate-500 lg:block">s/d</div>

              <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm transition focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100">
                <span className="text-slate-400">📅</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                  className="w-full min-w-[150px] bg-transparent text-sm text-slate-700 outline-none"
                />
              </div>
            </div>

            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-3">
              <div className="flex min-w-0 flex-1 items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm transition focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100">
                <span className="text-xs text-slate-400">⌕</span>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Cari jenis, program, donatur..."
                  className="w-full min-w-0 bg-transparent text-sm text-slate-700 placeholder-slate-400 outline-none"
                />
              </div>

              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsExportOpen((prev) => !prev)}
                  className="inline-flex items-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 active:scale-95"
                >
                  <span>📊 Export</span>
                  <span className="text-xs">{isExportOpen ? '▲' : '▼'}</span>
                </button>

                {isExportOpen && (
                  <div className="absolute right-0 top-full z-20 mt-2 w-48 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_18px_35px_rgba(15,23,42,0.12)]">
                    <button
                      type="button"
                      onClick={() => {
                        handleExportExcel();
                        setIsExportOpen(false);
                      }}
                      className="block w-full px-4 py-3 text-left text-sm font-medium text-slate-700 transition hover:bg-blue-50"
                    >
                      📈 Export Excel
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        handleExportPdf();
                        setIsExportOpen(false);
                      }}
                      className="block w-full px-4 py-3 text-left text-sm font-medium text-slate-700 transition hover:bg-blue-50"
                    >
                      📄 Export PDF
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_14px_34px_rgba(15,23,42,0.05)] transition hover:shadow-[0_20px_40px_rgba(15,23,42,0.08)]">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs uppercase tracking-[0.22em] text-slate-500">Total Pemasukan</p>
              <span className="rounded-full bg-blue-950 px-3 py-1 text-[11px] font-semibold text-white">Nominal</span>
            </div>
            <p className="mt-4 text-2xl font-bold text-slate-900">{formatCurrency(laporan.grossTotalNominal)}</p>
            <p className="mt-3 text-xs text-slate-500">Total pemasukan kotor dari semua transaksi sukses sebelum hak amil dan penyaluran.</p>
          </div>

          <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_14px_34px_rgba(15,23,42,0.05)] transition hover:shadow-[0_20px_40px_rgba(15,23,42,0.08)]">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs uppercase tracking-[0.22em] text-slate-500">Jumlah Transaksi</p>
              <span className="rounded-full bg-amber-400 px-3 py-1 text-[11px] font-semibold text-slate-950">Count</span>
            </div>
            <p className="mt-4 text-2xl font-bold text-slate-900">{laporan.totalTransaksi}</p>
            <p className="mt-3 text-xs text-slate-500">Transaksi terfilter</p>
          </div>

          <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_14px_34px_rgba(15,23,42,0.05)] transition hover:shadow-[0_20px_40px_rgba(15,23,42,0.08)]">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs uppercase tracking-[0.22em] text-slate-500">Sisa Hak Amil</p>
              <span className="rounded-full bg-emerald-600 px-3 py-1 text-[11px] font-semibold text-white">Sisa (40%)</span>
            </div>
            <p className="mt-4 text-2xl font-bold text-slate-900">{formatCurrency(laporan.totalHakAmilSisa)}</p>
          </div>

          <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_14px_34px_rgba(15,23,42,0.05)] transition hover:shadow-[0_20px_40px_rgba(15,23,42,0.08)]">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs uppercase tracking-[0.22em] text-slate-500\">Total DT Peduli</p>
              <span className="rounded-full bg-indigo-600 px-3 py-1 text-[11px] font-semibold text-white\">60% Alokasi</span>
            </div>
            <p className="mt-4 text-2xl font-bold text-slate-900\">{formatCurrency(laporan.totalDtPeduli)}</p>
          </div>
        </div>

        <section>
          <div className="mb-6">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-amber-600">Visualisasi Data</p>
            <h2 className="mt-1 text-xl font-bold text-slate-900">Ringkasan grafis saldo donasi</h2>
            <p className="mt-2 text-sm text-slate-500">Komposisi menampilkan sisa saldo per kategori setelah hak amil dan penyaluran.</p>
          </div>
          <div className="grid gap-6 xl:grid-cols-2">
            <VerticalBarChart data={laporan.monthlyChartData} />
            <DonutChart data={laporan.categoryTotals} total={laporan.totalNominal} />
          </div>
        </section>

        <section>
          <div className="mb-6">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-green-600">Analisis Khusus</p>
            <h2 className="mt-1 text-xl font-bold text-slate-900">Hak amil zakat dan infaq</h2>
          </div>
          <div className="grid gap-6 xl:grid-cols-2">
            <HakAmilChart 
              zakatData={laporan.totalZakat} 
              infaqData={laporan.totalInfaq}
              totalZakatInfaq={laporan.totalZakatInfaq} 
            />
            <PembagianHakAmilChart 
              zakatData={laporan.totalZakat} 
              infaqData={laporan.totalInfaq}
            />
          </div>
        </section>

        <section className="rounded-[30px] border border-slate-200 bg-white shadow-[0_18px_45px_rgba(15,23,42,0.06)]">
          <div className="border-b border-slate-100 px-8 py-6">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Detail Transaksi</p>
            <h2 className="mt-2 text-xl font-bold text-slate-900">Daftar transaksi sukses</h2>
            <p className="mt-2 text-sm text-slate-600">Menampilkan semua transaksi dengan status sukses </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead className="bg-[linear-gradient(180deg,#f8fafc_0%,#ffffff_100%)]">
                <tr>
                  <th className="px-8 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">No</th>
                  <th className="px-8 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">Kode</th>
                  <th className="px-8 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">Tanggal</th>
                  <th className="px-8 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">Donatur</th>
                  <th className="px-8 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">Jenis Donasi</th>
                  <th className="px-8 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">Program</th>
                  <th className="px-8 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">Nominal</th>
                  <th className="px-8 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredTransactions.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-8 py-12 text-center text-sm text-slate-500">
                      Tidak ada transaksi yang sesuai dengan filter.
                    </td>
                  </tr>
                ) : (
                  filteredTransactions.map((trx, index) => (
                    <tr key={trx.id || trx.kode_transaksi || `${index}-${resolveTransactionDate(trx)}`} className="transition hover:bg-blue-50/50">
                      <td className="px-8 py-4 text-sm font-medium text-slate-600">{index + 1}</td>
                      <td className="px-8 py-4 text-sm font-medium text-slate-800">{trx.kode_transaksi || '-'}</td>
                      <td className="px-8 py-4 text-sm text-slate-600">{formatDate(resolveTransactionDate(trx))}</td>
                      <td className="px-8 py-4 text-sm text-slate-600">{resolveDonatur(trx)}</td>
                      <td className="px-8 py-4 text-sm text-slate-600">{getJenisMeta(trx).nama}</td>
                      <td className="px-8 py-4 text-sm text-slate-600">{resolveDetailDonasi(trx)}</td>
                      <td className="px-8 py-4 text-sm font-semibold text-slate-900">{formatCurrency(resolveAmount(trx))}</td>
                      <td className="px-8 py-4 text-sm text-slate-600">
                        <span className="inline-block rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-700">
                          {resolveStatusLabel(trx)}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}

export default LaporanDonasiAdmin;
